import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Eye, EyeOff, Hourglass, Lock } from "lucide-react";

/**
 * Iter31n → iter86.31.7 → iter87.229 (user-reported: toggles "seemed to
 * not be doing anything").
 *
 * Root cause: for most of these shapes the sport is 1:1 with its
 * template (baseball → mlb_playoffs_api, basketball → nba_playoffs_api,
 * olympics, custom, etc.) — TournamentSetup's Step-1 sport tile is
 * gated ONLY by the platform-level Template Visibility override
 * (`system_settings.template_visibility`, Commons Console → Product
 * Ops → Templates, platform_admin-only), not by this tenant-scoped
 * `disabled_templates` list. Flipping a tile here had zero visible
 * effect on those shapes. Rather than half-work, this card is now
 * READ-ONLY everywhere: it shows the actual effective state (Live /
 * Coming soon / Hidden), sourced from the same platform-level
 * visibility map the wizard itself reads. No more clicking — the
 * tenant-scoped `disabled_templates` toggle this card used to write
 * is retired (it never gated these sport-tile-level shapes anyway).
 */
const SHAPE_TILES = [
    { key: "soccer_season", label: "Soccer Season", icon: "⚽", keys: ["premier_league_api", "la_liga_api", "champions_league_api", "club_season_api"], desc: "Full club season — W/D/L points all year. Covers every bound league: Premier League, La Liga, Champions League + any league added via Event Bindings (Ligue 1, MLS…)." },
    { key: "soccer_tournament", label: "Soccer Tournament", icon: "🌍", keys: ["world_cup", "world_cup_api"], desc: "Group stage + knockout (World Cup format). 48 teams, 12 groups, bracket to the final." },
    { key: "soccer_ko", label: "Soccer KO", icon: "⚔️", keys: ["world_cup_knockout", "world_cup_ko_api"], desc: "Knockout-only bracket — skip the group stage, straight to the rounds." },
    { key: "golf_lowest_avg", label: "Golf — Lowest Average", icon: "⛳", keys: ["golf_pickem_lowest_avg_api"], desc: "Tiered-pot pick'em over the OWGR top 30 (overlap allowed). Lowest average strokes per round played wins." },
    { key: "golf_position_draft", label: "Golf — Position Draft", icon: "🏌️", keys: ["golf_position_draft_api"], desc: "Auto-snake draft of the OWGR top 30. Points for making the cut + final positions in the top 20, plus a Champions Pick bonus." },
    { key: "football_season", label: "NFL Season", icon: "🏈", keys: ["nfl_season_api"], desc: "NFL Season Pot Pick'em — draft 8 clubs across 4 pots with multipliers (1x–2x), Champions Pick for the NFL's best record, plus the Weekly Spread Challenge." },
    { key: "football_nfl_boost", label: "NFL Weekly Pick'em", icon: "🏈", keys: ["nfl_boost_api"], desc: "NFL Weekly Spread Boost — no draft, no team assignments. Every crewmate picks its chosen number of spread bets from the full weekly slate. Regular season only (Weeks 1–18)." },
    { key: "soccer_weekly_boost", label: "Soccer Weekly Pick'em", icon: "⚽", keys: ["epl_boost_api", "la_liga_boost_api"], desc: "Weekly Spread Boost for soccer — no draft, no team assignments. Every crewmate picks Premier League or La Liga spread bets from that week's slate." },
    { key: "soccer_nwsl_playoffs", label: "NWSL Playoffs", icon: "⚽", keys: ["nwsl_playoffs_api"], desc: "NWSL Playoffs (API-Football) — pure pick'em: Champions Pick + Dark Horse Pick + Playoff Predictor. No draft, no rostered teams." },
    { key: "soccer_mls_playoffs", label: "MLS Playoffs", icon: "⚽", keys: ["mls_playoffs_api"], desc: "MLS Playoffs (API-Football) — pure pick'em: Champions Pick + Dark Horse Pick + Play-In Bonus (pre-season), Round One Series Predictor + reused Playoff Predictor (in-season). No draft, no rostered teams." },
    { key: "soccer_ucl_playoffs", label: "UCL Playoffs", icon: "⚽", keys: ["ucl_playoffs_api"], desc: "UCL Playoffs (API-Football) — pure pick'em: UCL Champion + Dark Horse Pick (pre-season), Two-Leg Predictor on every 2-leg tie + Champions Predictor on the Final (in-season). No draft, no rostered teams." },
    { key: "basketball_ko", label: "Basketball KO", icon: "🏀", keys: ["march_madness"], desc: "March Madness — Men's & Women's 64-team brackets. Customizable per-round scoring." },
    { key: "basketball_playoffs", label: "NBA Playoffs", icon: "🏀", keys: ["nba_playoffs_api"], desc: "NBA Playoffs (Highlightly, same RapidAPI key as MLB) — Team Pick Pot (8 teams, 4 seed tranches, Play-In counts) + Champions Pick + Series Predictor." },
    { key: "baseball_ko", label: "MLB Playoffs", icon: "⚾", keys: ["mlb_playoffs_api"], desc: "MLB Playoffs (Highlightly) — Team Pick Pot (6 teams, 3 seed tranches) + Champions Pick (AL/NL/WS) + Series Predictor." },
    { key: "football_ko", label: "NFL/CFB Playoffs", icon: "🏈", keys: ["nfl_playoffs_api", "cfb_playoffs_api"], desc: "NFL Playoffs (14-team, API-Sports) — pure pick'em: Super Bowl Champion (1-2 picks) + Dark Horse Picks (AFC/NFC seed 5-7, +pts/win) + Playoff Predictor. CFB Playoffs (12-team) — Team Pick Pot (1/2/2 across 3 pots), round-scored bracket (6→8→10→12), plus the Playoff Predictor between rounds." },
    { key: "olympics", label: "Olympics", icon: "🏅", keys: ["olympics"], desc: "Group + medal rounds. Coming soon — not yet built out." },
    // iter87.570 (user-reported — "don't see Squares on template
    // availability") — Squares is its own top-level sport in the
    // Tournament Setup wizard (TemplatePicker.jsx), Pro+/comp-only, but
    // was never added here, so Site Admin had no read-only view of its
    // effective state at all.
    { key: "squares", label: "Squares (Pro+ only)", icon: "🔲", keys: ["nfl_squares_manual_api", "nfl_squares_auto_api", "cfb_squares_manual_api", "cfb_squares_auto_api"], desc: "Pro+/comp rivalries only. 10x10 digits board on one marquee NFL or CFB game — Pick Your Boxes or Luck of the Draw, no draft, no bracket." },
    { key: "custom", label: "Custom", icon: "🛠️", keys: ["custom"], desc: "Build any format from scratch — groups, knockout, or round-robin. Manual entry is open to every rivalry; `custom_enabled` only gates the wizard tile's visibility + the GotSport/CSV/Portfolio data-source integrations, not tournament creation itself." },
];

const STATE_META = {
    live:        { label: "Live",        icon: Eye,       color: "text-neon" },
    coming_soon: { label: "Coming soon", icon: Hourglass, color: "text-zinc-400" },
    closed:      { label: "Closed",      icon: Lock,      color: "text-amber-400" },
    hidden:      { label: "Hidden",      icon: EyeOff,    color: "text-rose-400" },
};
// Worst-first tiebreak when a tile's member keys carry different
// non-live states (e.g. one conference hidden, another closed).
const STATE_RANK = { hidden: 3, closed: 2, coming_soon: 1, live: 0 };

// Worst-wins across a tile's member keys — mirrors the same
// `stateFor()` resolution TournamentSetup's wizard applies to its
// sport tiles: the platform-level Template Visibility map is the sole
// source of truth for whether a shape is selectable.
function effectiveState(keys, visibilityOverrides) {
    let worst = "live";
    for (const k of keys) {
        const v = visibilityOverrides[k] || "live";
        if (v === "hidden") return "hidden";
        if (STATE_RANK[v] > STATE_RANK[worst]) worst = v;
    }
    return worst;
}

export default function SiteAdminTemplateAvailability() {
    const [overrides, setOverrides] = useState({});
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const visibility = await api.getTemplateVisibility();
            setOverrides(visibility?.overrides && typeof visibility.overrides === "object" ? visibility.overrides : {});
        } catch {
            toast.error("Couldn't load template availability");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="template-availability-card">
            <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-cyber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100">Template Availability</h3>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Read-only — shows what rivalry admins actually see on the tournament setup screen right
                now. Enable/disable decisions are made in Commons Console → Product Ops → Templates.
            </p>

            <div className="grid sm:grid-cols-2 gap-2 pt-2">
                {SHAPE_TILES.map((t) => {
                    const state = effectiveState(t.keys, overrides);
                    const meta = STATE_META[state];
                    const Icon = meta.icon;
                    return (
                        <div
                            key={t.key}
                            data-testid={`template-availability-${t.key}-status`}
                            className={`text-left p-3 border-2 rounded-md flex items-start gap-3 ${
                                state === "live" ? "border-cyber/40 bg-cyber/5" : "border-[#333] bg-ink/40 opacity-70"
                            }`}
                        >
                            <div className="text-2xl shrink-0">{t.icon}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                    <span className="font-bold text-sm">{t.label}</span>
                                    <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} />
                                </div>
                                <p className="text-[10px] text-zinc-500 leading-relaxed">{t.desc}</p>
                                <p className={`text-[10px] mt-1 font-bold uppercase tracking-wider ${meta.color}`}>
                                    {meta.label}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {loading && <p className="text-xs text-zinc-500">Loading...</p>}
        </div>
    );
}
